import type { ObjectId } from '@racemap/sdk/schema/base';
import { LogisticsState } from '@racemap/utilities/consts/trackers';
import { isDefined } from '@racemap/utilities/functions/validation';
import { type FC, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useCurrentUser } from '../../../lib/customHooks';
import { useStore } from '../../../store/reducers';
import { HStack } from '../../BasicComponents/MetaComponent';
import { TrackerUpdateCount } from '../../BasicComponents/TrackerUpdateCount';
import { FormSelect } from '../../FormComponents';

interface Props {
  trackerIds: Array<ObjectId>;
  show: boolean;
  onClose: () => void;
}

export const TrackerLogisticsModal: FC<Props> = ({ trackerIds, show = false, onClose }) => {
  const currentUser = useCurrentUser();
  const trackers = useStore((s) => s.trackers.items);
  const trackersToUse = trackerIds.map((t) => trackers.get(t.toString())).filter(isDefined);
  const [newLogisticsState, setNewLogisticsState] = useState(
    trackersToUse != null && trackersToUse[0]?.logistics === LogisticsState.inStock
      ? LogisticsState.outOfStock
      : LogisticsState.inStock,
  );

  const { updateTracker } = useStore((s) => s.trackers.actions);

  const handleOkClick = async () => {
    if (currentUser == null || trackersToUse == null) return;

    for (const tracker of trackersToUse) {
      if (tracker == null) continue;
      await updateTracker(tracker.id, { logistics: newLogisticsState });
    }
    onClose();
    toast.success(`${trackersToUse?.length || 0} trackers updated!`);
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Change the logistics state of the trackers!</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <FormSelect<LogisticsState> value={newLogisticsState} onChange={setNewLogisticsState}>
          <option value={LogisticsState.inStock}>In stock</option>
          <option value={LogisticsState.outOfStock}>Out of stock</option>
        </FormSelect>
      </Modal.Body>
      <Modal.Footer>
        <HStack style={{ width: '100%' }}>
          <TrackerUpdateCount
            targetTrackers={trackersToUse || []}
            total={trackersToUse?.length || 0}
          />
          <Button onClick={handleOkClick} disabled={newLogisticsState == null}>
            Ok
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
};
